import { Rate } from "@/types/hotel";
import { create } from "zustand";
import { persist } from "zustand/middleware";

type ChildType = {
  name: string;
  age: string;
  lastName?: string;
};

type AdultType = {
  name: string;
  lastName: string;
};

export interface RoomsType {
  rateKey: string;
  maxAdult?: number | null;
  maxChild?: number | null;
  roomName?: string;
  roomCode?: string;
  currency?: string;
  hotelId?: string;
  adults: AdultType[];
  children: ChildType[];
  id?: string;
}

export interface RoomsCountType {
  cancellationPolicies?: any;
  rate: Rate;
  count: number;
  name: string;
  roomCode: string;
  currency: string;
  hotelId: string;
  id?: string;
  image: string;
  hotelName: string;
  hotelAddress: string;
  hotelRate: string;
  roomFacility: any[];
}

export interface Holder {
  name: string;
  lastName: string;
  email: string;
  token: string;
  tel: string;
  id: string;
}

export interface RoomsHolder {
  name: string;
  lastName: string;
  email: string;
}
export interface Discount {
  discount: string;
  totalDiscount: number;
  totalPrice: number;
}

type RoomsDataState = {
  holder: Holder;
  discounts: Discount[];
  roomsData: RoomsType[];
  roomsCount: RoomsCountType[];
  roomsHolder: RoomsHolder;
  currency: string;
  orderId: string;
  total: number;
  totalAfterDiscount: number | null;
  discountPrice: number;
  discountCode: string;
  removeRoomByIndex: (index: number) => void;
  setRoomsData: (data: RoomsType[]) => void;
  addEmptyRoom: ({
    roomName,
    rateKey,
    maxAdult,
    maxChild,
    roomCode,
  }: {
    roomName: string;
    rateKey?: string;
    maxAdult?: number | null;
    maxChild?: number | null;
    roomCode?: string;
  }) => void;
  addAdult: (roomIndex: number) => void;
  setHolder: (holder: Holder) => void;
  addChild: (roomIndex: number) => void;
  removeAdult: (roomIndex: number) => void;
  removeChild: (roomIndex: number) => void;
  editChildAge: (roomIndex: number, childIndex: number, newAge: string) => void;
  setRateKey: (roomIndex: number, rateKey: string) => void;
  setRoomsCount: (room: RoomsCountType) => void;
  removeRoomsCount: (index: number) => void;
  removeRoomsByRateKey: (rateKey: string) => void;
  removeRoomsByRoomCode: (roomCode: string) => void;
  resetRoomsCount: () => void;
  setAdultInfo: (
    roomIndex: number,
    adultIndex: number,
    key: "name" | "lastName",
    value: string,
  ) => void;
  setChildInfo: (
    roomIndex: number,
    childIndex: number,
    key: "name" | "lastName" | "age",
    value: string,
  ) => void;
  setHolderInfo: (key: keyof Holder, value: string) => void;
  setDiscounts: (discounts: Discount[]) => void;
  setRoomsHolder: (data: RoomsHolder) => void;
  setOrderId: (id: string) => void;
  setDiscountCode: (discountCode: string) => void;
  setTotalAfterDiscount: (totalAfterDiscount: number) => void;
  setDiscountPrice: (totalAfterDiscount: number) => void;
};

export const useRoomsStore = create<RoomsDataState>()(
  persist(
    (set) => ({
      discounts: [],
      holder: {
        name: "",
        email: "",
        lastName: "",
        tel: "",
        token: "",
        id: "",
      },
      setHolder: (data) =>
        set((state) => ({
          holder: data,
        })),
      roomsData: [],
      roomsCount: [],
      roomsHolder: { name: "", lastName: "", email: "" },
      orderId: "",
      total: 0,
      currency: "EUR",
      discountCode: "",
      totalAfterDiscount: null,
      discountPrice: 0,
      setDiscounts: (discounts) => set((state) => ({ discounts })),
      setRoomsData: (data) =>
        set((state) => ({
          roomsData: data,
        })),
      removeRoomByIndex: (index) =>
        set((state) => {
          if (state.roomsData.length === 1) {
            return { roomsData: state.roomsData };
          }
          return {
            roomsData: state.roomsData.filter((_, i) => i !== index),
          };
        }),
      addEmptyRoom: ({
        rateKey = "",
        maxAdult = null,
        maxChild = null,
        roomCode = "",
        roomName = "",
      }) =>
        set((state) => {
          if (state.roomsData.length === 5) {
            return { roomsData: [...state.roomsData] };
          }
          return {
            roomsData: [
              ...state.roomsData,
              {
                roomName,
                roomCode,
                maxChild,
                maxAdult,
                rateKey,
                adults: [{ name: "", lastName: "" }],
                children: [],
              },
            ],
          };
        }),
      addAdult: (roomIndex) =>
        set((state) => {
          if (
            (state?.roomsData?.[roomIndex]?.maxAdult &&
              Number(state?.roomsData?.[roomIndex].maxAdult) <=
                state.roomsData[roomIndex]?.adults?.length) ||
            0
          ) {
            return { roomsData: state.roomsData };
          }
          return {
            roomsData: state.roomsData.map((room, i) => {
              if (i === roomIndex) {
                const newAdults = [...room.adults];
                newAdults.pop(); // Remove the last adult from the array
                return {
                  ...room,
                  adults: [...room.adults, { name: "", lastName: "" }],
                };
              }
              return room;
            }),
          };
        }),
      addChild: (roomIndex) =>
        set((state) => {
          if (
            state.roomsData[roomIndex].maxChild &&
            Number(state.roomsData[roomIndex].maxChild) <=
              state.roomsData[roomIndex].children.length
          ) {
            return { roomsData: state.roomsData };
          }
          return {
            roomsData: state.roomsData.map((room, i) => {
              if (i === roomIndex) {
                return {
                  ...room,
                  children: [
                    ...room.children,
                    { age: "2", name: "", lastName: "" },
                  ],
                };
              }
              return room;
            }),
          };
        }),
      removeAdult: (roomIndex) =>
        set((state) => {
          if (state.roomsData[roomIndex].adults.length === 1) {
            return { roomsData: state.roomsData };
          }

          return {
            roomsData: state.roomsData.map((room, i) => {
              if (i === roomIndex) {
                const newAdults = [...room.adults];
                newAdults.pop(); // Remove the last adult from the array
                return {
                  ...room,
                  adults: newAdults,
                };
              }
              return room;
            }),
          };
        }),
      removeChild: (roomIndex) =>
        set((state) => ({
          roomsData: state.roomsData.map((room, i) => {
            if (i === roomIndex) {
              return {
                ...room,
                children: room.children.slice(0, -1),
              };
            }
            return room;
          }),
        })),
      editChildAge: (roomIndex, childIndex, newAge) =>
        set((state) => ({
          roomsData: state.roomsData.map((room, i) => {
            if (i === roomIndex) {
              return {
                ...room,
                children: room.children.map((child, j) => {
                  if (j === childIndex) {
                    return {
                      ...child,
                      age: newAge,
                    };
                  }
                  return child;
                }),
              };
            }
            return room;
          }),
        })),
      setChildInfo: (
        roomIndex: number,
        childIndex: number,
        key: "name" | "lastName" | "age",
        value: string,
      ) =>
        set((state) => ({
          roomsData: state.roomsData.map((room, i) => {
            if (i === roomIndex) {
              const updatedChildren = room.children.map((child, j) => {
                if (j === childIndex) {
                  return {
                    ...child,
                    [key]: value,
                  };
                }
                return child;
              });
              return {
                ...room,
                children: updatedChildren,
              };
            }
            return room;
          }),
        })),
      setAdultInfo: (
        roomIndex: number,
        adultIndex: number,
        key: "name" | "lastName",
        value: string,
      ) =>
        set((state) => ({
          roomsData: state.roomsData.map((room, i) => {
            if (i === roomIndex) {
              const updatedAdults = room.adults.map((adult, j) => {
                if (j === adultIndex) {
                  return {
                    ...adult,
                    [key]: value,
                  };
                }
                return adult;
              });
              return {
                ...room,
                adults: updatedAdults,
              };
            }
            return room;
          }),
        })),
      setHolderInfo: (key: keyof Holder, value: string) =>
        set((state) => ({
          holder: {
            ...state.holder,
            [key]: value,
          },
        })),
      setRateKey: (roomIndex, rateKey) =>
        set((state) => ({
          roomsData: state.roomsData.map((room, i) => {
            if (i === roomIndex) {
              return {
                ...room,
                rateKey: rateKey, // Set the rateKey dynamically
              };
            }
            return room;
          }),
        })),
      setRoomsCount: (room) =>
        set((state) => ({
          roomsCount: [...state.roomsCount, room],
          total: +(state.total + +room.rate.net).toFixed(2),
          currency: room.rate.currency,
        })),
      removeRoomsCount: (index) =>
        set((state) => ({
          roomsCount: state.roomsCount.filter(
            (room, roomIndex) => index !== roomIndex,
          ),
        })),
      removeRoomsByRateKey: (rateKey) =>
        set((state) => ({
          roomsCount: state.roomsCount.filter(
            (room) => rateKey !== room.rate.rateKey,
          ),
          total: +(
            state.total -
            +state.roomsCount.find((room) => rateKey === room.rate.rateKey)
              ?.rate.net!
          ).toFixed(2),
        })),
      removeRoomsByRoomCode: (roomCode) =>
        set((state) => {
          const roomsToRemove = state.roomsCount.filter(
            (room) => room.roomCode === roomCode,
          );
          const totalToRemove = roomsToRemove.reduce(
            (total, room) => total + +room.rate.net,
            0,
          );
          const updatedRoomsCount = state.roomsCount.filter(
            (room) => room.roomCode !== roomCode,
          );
          return {
            roomsCount: updatedRoomsCount,
            total: +(state.total - totalToRemove).toFixed(2),
          };
        }),
      resetRoomsCount: () => set(() => ({ roomsCount: [], total: 0 })),
      setRoomsHolder: (data) => set(() => ({ roomsHolder: data })),
      setOrderId: (id) => set(() => ({ orderId: id })),
      setDiscountCode: (discountCode) => set(() => ({ discountCode })),
      setTotalAfterDiscount: (totalAfterDiscount) =>
        set(() => ({ totalAfterDiscount })),
      setDiscountPrice: (discountPrice) => set(() => ({ discountPrice })),
    }),
    { name: "roomData" },
  ),
);
